import { Badge, Button, Icon, Input, Typography } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Card, MaskInput, dateMaskTrack } from '@hero/atoms';
import { trackerManager } from '@hero/clients';
import {
  MAX_EMAIL_LENGTH,
  MAX_FIRST_NAME_LENGTH,
  MAX_LAST_NAME_LENGTH,
  TRACKER_EVENT,
  dateLabelFormatDay,
  dateLabelFormatDayWithYear
} from '@hero/constants';
import { useDeleteModalTemplate, useIsAutomationEnabled, useValidInput } from '@hero/hooks';
import { DetailGroup, DetailItem, OrderStatusBadge, TagSelect } from '@hero/molecules';
import {
  useGetContact,
  useGetTagList,
  useRemoveContact,
  useUpdateContact
} from '@hero/services/contact-service/hooks';
import { useGetOrders } from '@hero/services/order-service/hook';
import { isNameValid } from '@hero/utils';
import { checkFormatContainsYear } from '@hero/utils/dateUtils';

import * as S from './style';

function AppreciationListItem({ name, status }) {
  return (
    <S.AppreciationItem>
      <Typography variant="body2" lineClamp={1}>
        {name}
      </Typography>
      <OrderStatusBadge status={status} />
    </S.AppreciationItem>
  );
}

function FirstNameEditInput({ value, inputProps, label }) {
  return (
    <Input
      value={value}
      inputProps={{ ...inputProps, maxLength: MAX_FIRST_NAME_LENGTH }}
      maxLength={30}
      error={!isNameValid(value)}
      formItem={{
        label,
        name: 'first_name',
        validations: {
          required: {
            required: true
          }
        }
      }}
    />
  );
}

function LastNameEditInput({ value, inputProps, label }) {
  return (
    <Input
      value={value}
      inputProps={{ ...inputProps, maxLength: MAX_LAST_NAME_LENGTH }}
      maxLength={30}
      error={!isNameValid(value)}
      formItem={{
        label,
        name: 'last_name',
        validations: {
          required: {
            required: true
          }
        }
      }}
    />
  );
}

function EmailAddressEditInput({ value, inputProps, label, error }) {
  return (
    <Input
      value={value}
      inputProps={{ ...inputProps, maxLength: MAX_EMAIL_LENGTH }}
      type="email"
      name="email"
      label={label}
      error={error}
    />
  );
}

function BirthdayEditInput({ value, setValue, label }) {
  return (
    <MaskInput
      value={value}
      onChange={e => setValue(e.target.value)}
      mask={'mm/dd/yyyy'}
      track={dateMaskTrack}
      placeholder={label}
      error={value.length < 5}
      replacement={{ d: /\d/, m: /\d/, y: /\d/ }}
    />
  );
}

function WorkAnniversaryEditInput({ value, setValue, label }) {
  return (
    <MaskInput
      value={value}
      onChange={e => setValue(e.target.value)}
      mask={'mm/dd/yyyy'}
      error={value.length < 5}
      track={dateMaskTrack}
      placeholder={label}
      replacement={{ d: /\d/, m: /\d/, y: /\d/ }}
    />
  );
}

function TagsEditInput({ value, setValue }) {
  return (
    <TagSelect
      styles={{
        control: provided => ({
          ...provided,
          height: 'auto',
          padding: '4px 0'
        }),
        valueContainer: provided => ({
          ...provided,
          width: 'min-content'
        })
      }}
      value={value}
      onChange={setValue}
    />
  );
}

function ContactDetailOverview({ setActiveTab }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const isAutomationEnabled = useIsAutomationEnabled();

  const { data: contactData } = useGetContact({ id });
  const { data: tagsResponse } = useGetTagList(
    {
      filters: {
        ids: contactData?.tags?.map(tag => tag.id)
      },
      options: {
        includeAutomations: true
      }
    },
    {
      enabled: !!contactData?.tags
    }
  );

  const includeAutomation = useMemo(() => {
    return !!tagsResponse?.docs?.filter?.(doc => !!doc?.automations?.length)?.length;
  }, [tagsResponse]);

  const { showDeleteModal } = useDeleteModalTemplate({
    confirmText: 'DELETE CONTACT',
    title: t('areYouSureYouWantDelete', { type: t('contact').toLowerCase() }),
    message: t('deleteContactConfirmMessage'),
    notification: includeAutomation
      ? {
          message: t('activeAutomationContactInfo', {
            firstName: contactData?.firstName
          }),
          status: 'warning'
        }
      : undefined
  });

  const { data: recognitionOrders } = useGetOrders(
    {
      filters: {
        type: 'recognition',
        email: contactData?.email
      },
      options: {
        populate: true
      },
      pagination: {
        limit: 3
      }
    },
    { enabled: !!contactData?.id }
  );

  const { data: automationOrders } = useGetOrders(
    {
      filters: {
        type: 'automation',
        email: contactData?.email
      },
      options: {
        populate: true
      },
      pagination: {
        limit: 3
      }
    },
    { enabled: !!contactData?.id }
  );

  const { mutateAsync: updateContact } = useUpdateContact();
  const { mutateAsync: removeContact } = useRemoveContact();

  const {
    value: firstName,
    inputProps: firstNameProps,
    setValue: firstNameSetValue,
    hasError: isFirstNameValid
  } = useValidInput(contactData?.firstName, isNameValid);
  const {
    value: lastName,
    inputProps: lastNameProps,
    setValue: lastNameSetValue,
    hasError: isLastNameValid
  } = useValidInput(contactData?.lastName, isNameValid);
  const {
    value: email,
    inputProps: emailProps,
    setValue: emailSetValue,
    hasError: isEmailValid
  } = useValidInput(contactData?.email);

  const [birthday, setBirthday] = useState();
  const [anniversary, setAnniversary] = useState();

  const sendUpdateContact = async updateContactBody => {
    await updateContact({
      id,
      body: updateContactBody
    });

    trackerManager.emit(TRACKER_EVENT.contactUpdated);
  };

  useEffect(() => {
    if (contactData?.firstName) {
      firstNameSetValue(contactData?.firstName);
      lastNameSetValue(contactData?.lastName);
      emailSetValue(contactData?.email);

      if (contactData?.birthday) setBirthday(contactData?.birthday?.full);
      if (contactData?.anniversary) setAnniversary(contactData?.anniversary?.full);
    }
  }, [contactData]);

  const [selectedTags, setSelectedTags] = useState();

  useEffect(() => {
    const tags = contactData?.tags?.map(tag => ({ label: tag.name, value: tag.id })) || [];
    setSelectedTags(tags);
  }, [contactData]);

  const userFullName = `${contactData?.firstName || ''} ${contactData?.lastName || ''}`;

  const groupHeader = (
    <S.HeaderGroup>
      <Typography variant="body1Bold">{userFullName}</Typography>
      <S.VerticalLine />
      <Button
        variant="link"
        color="error"
        onClick={() => {
          showDeleteModal({
            onSubmit: () => {
              removeContact(contactData?.id);
            }
          });
        }}
        startIcon={<Icon name="delete" />}
      >
        {t('deleteContact')}
      </Button>
    </S.HeaderGroup>
  );

  const getYearStatusInDate = type => {
    if (checkFormatContainsYear(type)) return dateLabelFormatDayWithYear;
    return dateLabelFormatDay;
  };

  return (
    <S.ContactDetailWrapper>
      <S.ContentBody>
        <S.ContactDetail>
          <Card>
            <DetailGroup header={groupHeader}>
              <DetailItem
                title={`${t('firstName')}:`}
                withBorder
                editable
                bodyStyle={{ padding: '16px 0' }}
                saveDetail={() => sendUpdateContact({ firstName })}
                isCTADisabled={isFirstNameValid || !isNameValid(firstName)}
                closeWithoutSave={() => {
                  firstNameSetValue(contactData?.firstName);
                }}
                editInput={() => (
                  <FirstNameEditInput
                    value={firstName}
                    inputProps={firstNameProps}
                    label={t('firstName')}
                  />
                )}
              >
                {firstName}
              </DetailItem>
              <DetailItem
                title={`${t('lastName')}:`}
                withBorder
                editable
                bodyStyle={{ padding: '16px 0' }}
                saveDetail={() => sendUpdateContact({ lastName })}
                isCTADisabled={isLastNameValid || !isNameValid(lastName)}
                closeWithoutSave={() => {
                  lastNameSetValue(contactData?.lastName);
                }}
                editInput={() => (
                  <LastNameEditInput
                    value={lastName}
                    inputProps={lastNameProps}
                    label={t('lastName')}
                  />
                )}
              >
                {lastName}
              </DetailItem>
              <DetailItem
                title={`${t('emailAddress')}:`}
                withBorder
                editable
                bodyStyle={{ padding: '16px 0' }}
                saveDetail={() => sendUpdateContact({ email })}
                isCTADisabled={isEmailValid}
                editInput={() => (
                  <EmailAddressEditInput
                    value={email}
                    error={isEmailValid}
                    inputProps={emailProps}
                    label={t('emailAddress')}
                  />
                )}
              >
                {email}
              </DetailItem>
              <DetailItem
                title={`${t('birthday')}:`}
                withBorder
                editable
                bodyStyle={{ alignItems: 'center', padding: '16px 0' }}
                saveDetail={() => {
                  sendUpdateContact({ birthday });
                }}
                editInput={() => (
                  <BirthdayEditInput
                    value={birthday}
                    setValue={setBirthday}
                    label={t('dayAndMonthOrYearPlaceholder')}
                  />
                )}
              >
                {/* #TODO don't use the dateFormat function  */}
                {birthday ? dayjs(birthday).format(getYearStatusInDate(birthday)) : <S.EmptyLine />}
              </DetailItem>
              <DetailItem
                title={`${t('workAnniversary')}:`}
                withBorder
                editable
                bodyStyle={{ alignItems: 'center', padding: '16px 0' }}
                saveDetail={() => {
                  sendUpdateContact({ anniversary });
                }}
                editInput={() => (
                  <WorkAnniversaryEditInput
                    value={anniversary}
                    setValue={setAnniversary}
                    label={t('dayAndMonthOrYearPlaceholder')}
                  />
                )}
              >
                {/* #TODO don't use the dateFormat function */}
                {anniversary ? (
                  dayjs(anniversary).format(getYearStatusInDate(anniversary))
                ) : (
                  <S.EmptyLine />
                )}
              </DetailItem>
              <DetailItem
                title={`${t('tags')}:`}
                editable
                bodyStyle={{ padding: '16px 0' }}
                saveDetail={() => sendUpdateContact({ tags: selectedTags })}
                editInput={() => <TagsEditInput value={selectedTags} setValue={setSelectedTags} />}
              >
                <S.TagsContainer>
                  {contactData?.tags?.map(tag => (
                    <Badge color="bgWeak" variant="contained" key={tag.id} size="sm" rounded>
                      {tag.name}
                    </Badge>
                  ))}
                </S.TagsContainer>
              </DetailItem>
            </DetailGroup>
          </Card>
        </S.ContactDetail>
        <S.Appreciations>
          <S.AppreciationDetail>
            <Card>
              <S.FeatureBox>
                <S.HeaderGroup style={{ marginBottom: 0 }}>
                  <Typography variant="body1Bold">{t('recognitions')}</Typography>
                  <S.VerticalLine />
                  <Button
                    variant="link"
                    onClick={() => {
                      setActiveTab('recognitions');
                    }}
                  >
                    {t('seeAll')}
                  </Button>
                </S.HeaderGroup>
                <Typography
                  style={{ marginBottom: 16 }}
                  color="fg"
                  emphasis="weaker"
                  variant="caption1"
                >
                  {t('recentRecognitionSendTo', { firstName })}
                </Typography>
                <S.FeatureBody>
                  {recognitionOrders?.docs?.length > 0 ? (
                    recognitionOrders.docs.map(recognitionOrder => (
                      <AppreciationListItem
                        key={recognitionOrder.id}
                        name={recognitionOrder?.recognition?.name}
                        status={recognitionOrder.giftStatus}
                      />
                    ))
                  ) : (
                    <>
                      <Typography variant="body2">
                        {t('neverBeenSendRecognition', { firstName })}
                      </Typography>
                    </>
                  )}
                </S.FeatureBody>
              </S.FeatureBox>
            </Card>
          </S.AppreciationDetail>
          {isAutomationEnabled && (
            <S.AppreciationDetail>
              <Card>
                <S.FeatureBox>
                  <S.HeaderGroup style={{ marginBottom: 0 }}>
                    <Typography variant="body1Bold">{t('automations')}</Typography>
                    <S.VerticalLine />
                    <Button
                      variant="link"
                      onClick={() => {
                        setActiveTab('automations');
                      }}
                    >
                      {t('seeAll')}
                    </Button>
                  </S.HeaderGroup>
                  <Typography
                    color="fg"
                    emphasis="weaker"
                    style={{ marginBottom: 16 }}
                    variant="caption1"
                  >
                    {t('activeAutomationIsPartOf', { firstName })}
                  </Typography>
                  <S.FeatureBody>
                    {automationOrders?.docs?.length > 0 ? (
                      automationOrders.docs.map(automationOrder => (
                        <AppreciationListItem
                          key={automationOrder.id}
                          name={automationOrder?.automation?.name}
                          status={automationOrder?.giftStatus}
                        />
                      ))
                    ) : (
                      <>
                        <Typography variant="body2">
                          {t('notPartOfActiveAutomation', { firstName })}
                        </Typography>
                      </>
                    )}
                  </S.FeatureBody>
                </S.FeatureBox>
              </Card>
            </S.AppreciationDetail>
          )}
        </S.Appreciations>
      </S.ContentBody>
    </S.ContactDetailWrapper>
  );
}

AppreciationListItem.propTypes = {
  name: PropTypes.string,
  status: PropTypes.string
};

ContactDetailOverview.propTypes = {
  setActiveTab: PropTypes.func
};

FirstNameEditInput.propTypes = {
  value: PropTypes.string,
  inputProps: PropTypes.any,
  label: PropTypes.node
};

LastNameEditInput.propTypes = {
  value: PropTypes.string,
  inputProps: PropTypes.any,
  label: PropTypes.node
};

EmailAddressEditInput.propTypes = {
  value: PropTypes.string,
  inputProps: PropTypes.any,
  label: PropTypes.node
};

BirthdayEditInput.propTypes = {
  value: PropTypes.string,
  setValue: PropTypes.func,
  label: PropTypes.node
};

WorkAnniversaryEditInput.propTypes = {
  value: PropTypes.string,
  setValue: PropTypes.func,
  label: PropTypes.node
};

TagsEditInput.propTypes = {
  value: PropTypes.string,
  setValue: PropTypes.func
};

export { ContactDetailOverview };
